"use client"

import React, { useEffect, useRef, useState } from 'react';
import { EditorContent, EditorContext, useEditor } from '@tiptap/react';

// --- Tiptap Core Extensions ---
import { StarterKit } from '@tiptap/starter-kit';
import ImageResize from 'tiptap-extension-resize-image';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import { TextAlign } from '@tiptap/extension-text-align';
import { Typography } from '@tiptap/extension-typography';
import { Highlight } from '@tiptap/extension-highlight';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { Selection } from '@tiptap/extensions';
import * as TableExtensions from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';

// --- Tiptap Markdown ---
import { Markdown } from 'tiptap-markdown';

// --- UI Primitives ---
import { Button } from '@/components/tiptap-ui-primitive/button';
import { Spacer } from '@/components/tiptap-ui-primitive/spacer';
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from '@/components/tiptap-ui-primitive/toolbar';

// --- Tiptap Node ---
import { ImageUploadNode } from '@/components/tiptap-node/image-upload-node/image-upload-node-extension';
import { HorizontalRule } from '@/components/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension';
import '@/components/tiptap-node/blockquote-node/blockquote-node.scss';
import '@/components/tiptap-node/code-block-node/code-block-node.scss';
import '@/components/tiptap-node/horizontal-rule-node/horizontal-rule-node.scss';
import '@/components/tiptap-node/list-node/list-node.scss';
import '@/components/tiptap-node/image-node/image-node.scss';
import '@/components/tiptap-node/heading-node/heading-node.scss';
import '@/components/tiptap-node/paragraph-node/paragraph-node.scss';

// --- Tiptap UI ---
import { HeadingDropdownMenu } from '@/components/tiptap-ui/heading-dropdown-menu';
import { ImageUploadButton } from '@/components/tiptap-ui/image-upload-button';
import { ListDropdownMenu } from '@/components/tiptap-ui/list-dropdown-menu';
import { BlockquoteButton } from '@/components/tiptap-ui/blockquote-button';
import { CodeBlockButton } from '@/components/tiptap-ui/code-block-button';
import {
  ColorHighlightPopover,
  ColorHighlightPopoverContent,
  ColorHighlightPopoverButton,
} from '@/components/tiptap-ui/color-highlight-popover';
import {
  LinkPopover,
  LinkContent,
  LinkButton,
} from '@/components/tiptap-ui/link-popover';
import { MarkButton } from '@/components/tiptap-ui/mark-button';
import { TextAlignButton } from '@/components/tiptap-ui/text-align-button';
import { UndoRedoButton } from '@/components/tiptap-ui/undo-redo-button';

// --- Icons ---
import { ArrowLeftIcon } from '@/components/tiptap-icons/arrow-left-icon';
import { HighlighterIcon } from '@/components/tiptap-icons/highlighter-icon';
import { LinkIcon } from '@/components/tiptap-icons/link-icon';
import { DownloadIcon, ArrowDownFromLineIcon, TableIcon } from 'lucide-react';

// --- Hooks ---
import { useIsBreakpoint } from '@/hooks/use-is-breakpoint';
import { useWindowSize } from '@/hooks/use-window-size';
import { useCursorVisibility } from '@/hooks/use-cursor-visibility';
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Components ---
import { ThemeToggle } from '@/components/tiptap-templates/simple/theme-toggle';

// --- Lib ---
import { handleImageUpload, MAX_FILE_SIZE } from '@/lib/tiptap-utils';

// --- Styles ---
import '@/components/tiptap-templates/simple/simple-editor.scss';

// --- UI Components ---
import { Textarea } from '@/components/ui/textarea';

// Keep the original TipTapEditorProps interface for backward compatibility
export interface TipTapEditorProps {
  content?: string;
  tiptapJson?: Record<string, unknown>;
  onChange?: (html: string, json: Record<string, unknown>) => void;
  placeholder?: string;
  editable?: boolean;
  // Feature flags - now ignored (markdown toggle removed)
  enableMarkdownToggle?: boolean;
  enableAiMarkdown?: boolean;
  showCharacterCount?: boolean;
  showMarkdownHint?: boolean;
  // AI Markdown specific - used for AI-generated content field
  aiMarkdown?: string;
  onAiMarkdownChange?: (markdown: string) => void;
  // Show convert button to convert Markdown to TipTap
  showConvertButton?: boolean;
}

// --- Table Insert Button Component ---
const TableInsertButton = () => {
  const { editor } = useTiptapEditor();
  
  return (
    <Button
      variant="ghost"
      onClick={() => {
        if (editor) {
          editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
        }
      }}
      title="Insert Table"
      disabled={!editor}
    >
      <TableIcon className="tiptap-button-icon" />
    </Button>
  );
};

// --- Toolbar Components ---

const MainToolbarContent = ({
  onHighlighterClick,
  onLinkClick,
  isMobile,
  showConvertButton,
  aiMarkdown,
  onInsertMarkdown,
  onConvertMarkdown,
}: {
  onHighlighterClick: () => void;
  onLinkClick: () => void;
  isMobile: boolean;
  showConvertButton?: boolean;
  aiMarkdown?: string;
  onInsertMarkdown?: () => void;
  onConvertMarkdown?: () => void;
}) => {
  const canConvert = showConvertButton && aiMarkdown && aiMarkdown.length > 0;

  return (
    <>
      <Spacer />

      <ToolbarGroup>
        <UndoRedoButton action="undo" />
        <UndoRedoButton action="redo" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <HeadingDropdownMenu levels={[1, 2, 3, 4]} portal={isMobile} />
        <ListDropdownMenu
          types={['bulletList', 'orderedList', 'taskList']}
          portal={isMobile}
        />
        <BlockquoteButton />
        <CodeBlockButton />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="bold" />
        <MarkButton type="italic" />
        <MarkButton type="strike" />
        <MarkButton type="code" />
        <MarkButton type="underline" />
        {!isMobile ? (
          <ColorHighlightPopover />
        ) : (
          <ColorHighlightPopoverButton onClick={onHighlighterClick} />
        )}
        {!isMobile ? <LinkPopover /> : <LinkButton onClick={onLinkClick} />}
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="superscript" />
        <MarkButton type="subscript" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <TextAlignButton align="left" />
        <TextAlignButton align="center" />
        <TextAlignButton align="right" />
        <TextAlignButton align="justify" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <ImageUploadButton text="Add" />
        <TableInsertButton />
      </ToolbarGroup>

      {showConvertButton && (
        <>
          <ToolbarSeparator />
          <ToolbarGroup>
            <Button
              variant="ghost"
              onClick={onInsertMarkdown}
              disabled={!canConvert}
              title={canConvert ? 'Insert Markdown at cursor' : 'Enter Markdown in AI Markdown field to insert'}
            >
              <DownloadIcon className="tiptap-button-icon" />
              <span className="ml-2">md-inst</span>
            </Button>
            <Button
              variant="ghost"
              onClick={onConvertMarkdown}
              disabled={!canConvert}
              title={canConvert ? 'Replace content with Markdown' : 'Enter Markdown in AI Markdown field to convert'}
            >
              <ArrowDownFromLineIcon className="tiptap-button-icon" />
              <span className="ml-2">md-convt</span>
            </Button>
          </ToolbarGroup>
        </>
      )}

      <Spacer />

      {isMobile && <ToolbarSeparator />}

      <ToolbarGroup>
        <ThemeToggle />
      </ToolbarGroup>
    </>
  );
};

const MobileToolbarContent = ({
  type,
  onBack,
}: {
  type: 'highlighter' | 'link';
  onBack: () => void;
}) => (
  <>
    <ToolbarGroup>
      <Button variant="ghost" onClick={onBack}>
        <ArrowLeftIcon className="tiptap-button-icon" />
        {type === 'highlighter' ? (
          <HighlighterIcon className="tiptap-button-icon" />
        ) : (
          <LinkIcon className="tiptap-button-icon" />
        )}
      </Button>
    </ToolbarGroup>

    <ToolbarSeparator />

    {type === 'highlighter' ? (
      <ColorHighlightPopoverContent />
    ) : (
      <LinkContent />
    )}
  </>
);

// --- Main Editor Component ---

export const TipTapEditor: React.FC<TipTapEditorProps> = ({
  content = '',
  tiptapJson,
  onChange,
  placeholder = 'Start writing your content...',
  editable = true,
  enableAiMarkdown = false,
  showCharacterCount = true,
  showMarkdownHint = true,
  aiMarkdown = '',
  onAiMarkdownChange,
  showConvertButton = false,
}) => {
  const isMobile = useIsBreakpoint();
  const { height } = useWindowSize();
  const [mobileView, setMobileView] = useState<'main' | 'highlighter' | 'link'>('main');
  const toolbarRef = useRef<HTMLDivElement>(null);

  const editor = useEditor({
    immediatelyRender: false,
    editable: editable,
    editorProps: {
      attributes: {
        autocomplete: 'off',
        autocorrect: 'off',
        autocapitalize: 'off',
        'aria-label': 'Main content area, start typing to enter text.',
        class: 'simple-editor',
      },
    },
    extensions: [
      StarterKit.configure({
        horizontalRule: false,
        link: {
          openOnClick: false,
          enableClickSelection: true,
        },
      }),
      HorizontalRule,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Highlight.configure({ multicolor: true }),
      ImageResize.configure({
        inline: false,
      }),
      Typography,
      Superscript,
      Subscript,
      Selection,
      Markdown.configure({
        html: true,
        tightLists: true,
        linkify: false,
        breaks: false,
      }),
      ImageUploadNode.configure({
        type: 'imageResize',
        accept: 'image/*',
        maxSize: MAX_FILE_SIZE,
        limit: 3,
        upload: handleImageUpload,
        onError: (error) => console.error('Upload failed:', error),
      }),
      TableExtensions.Table.configure({
        resizable: true,
      }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    content: tiptapJson || content,
    onUpdate: ({ editor }) => {
      if (onChange) {
        const html = editor.getHTML();
        const json = editor.getJSON();
        onChange(html, json as Record<string, unknown>);
      }
    },
  });

  // Update editor content when tiptapJson changes externally
  useEffect(() => {
    if (editor && tiptapJson && JSON.stringify(editor.getJSON()) !== JSON.stringify(tiptapJson)) {
      editor.commands.setContent(tiptapJson);
    }
  }, [tiptapJson, editor]);

  // Update editable state when prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(editable);
    }
  }, [editable, editor]);

  const rect = useCursorVisibility({
    editor,
    overlayHeight: toolbarRef.current?.getBoundingClientRect().height ?? 0,
  });

  useEffect(() => {
    if (!isMobile && mobileView !== 'main') {
      setMobileView('main');
    }
  }, [isMobile, mobileView]);

  // Calculate character count
  const characterCount = editor?.getText().length ?? 0;

  const handleAiMarkdownChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (onAiMarkdownChange) {
      onAiMarkdownChange(e.target.value);
    }
  };

  const handleInsertMarkdown = () => {
    if (editor && aiMarkdown && aiMarkdown.length > 0) {
      // Insert content at current cursor position
      editor.commands.insertContent(aiMarkdown);
      if (onChange) {
        const html = editor.getHTML();
        const json = editor.getJSON();
        onChange(html, json as Record<string, unknown>);
      }
    }
  };

  const handleConvertMarkdown = () => {
    if (editor && aiMarkdown && aiMarkdown.length > 0) {
      // Replace entire content with markdown
      editor.commands.setContent(aiMarkdown);
      if (onChange) {
        const html = editor.getHTML();
        const json = editor.getJSON();
        onChange(html, json as Record<string, unknown>);
      }
    }
  };

  // Render the editor with SimpleEditor's toolbar
  return (
    <div className="border border-border rounded-lg bg-background">
      <div className="simple-editor-wrapper">
        <EditorContext.Provider value={{ editor }}>
          <Toolbar
            ref={toolbarRef}
            style={{
              ...(isMobile
                ? {
                    bottom: `calc(100% - ${height - rect.y}px)`,
                  }
                : {}),
            }}
          >
            {mobileView === 'main' ? (
              <MainToolbarContent
                onHighlighterClick={() => setMobileView('highlighter')}
                onLinkClick={() => setMobileView('link')}
                isMobile={isMobile}
                showConvertButton={showConvertButton}
                aiMarkdown={aiMarkdown}
                onInsertMarkdown={handleInsertMarkdown}
                onConvertMarkdown={handleConvertMarkdown}
              />
            ) : (
              <MobileToolbarContent
                type={mobileView === 'highlighter' ? 'highlighter' : 'link'}
                onBack={() => setMobileView('main')}
              />
            )}
          </Toolbar>

          <EditorContent
            editor={editor}
            role="presentation"
            className="simple-editor-content"
          />
        </EditorContext.Provider>
      </div>

      {/* Footer - Character count and markdown hints */}
      {(showCharacterCount || showMarkdownHint) && (
        <div className="flex items-center justify-between px-4 py-2 border-t border-border text-xs text-muted-foreground">
          {showCharacterCount && <span>{characterCount} characters</span>}
          {showMarkdownHint && (
            <span className="text-muted-foreground">
              Tip: Use Markdown shortcuts like **bold**, *italic*, # heading
            </span>
          )}
        </div>
      )}

      {/* AI Markdown Section - shown below editor (not in toggle) */}
      {enableAiMarkdown && (
        <div className="p-4 border-t border-border">
          <label className="block text-sm font-medium mb-2">AI Markdown</label>
          <Textarea
            value={aiMarkdown}
            onChange={handleAiMarkdownChange}
            placeholder="Paste AI-generated Markdown content here..."
            rows={8}
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Alternative input for AI-generated content. This will be saved to the database.
          </p>
        </div>
      )}
    </div>
  );
};

export default TipTapEditor;
